/**
 * AuditDuplicatesDialog — modal that runs the exact-duplicate scan on a
 * county's V2 parcel collection and shows the results. When duplicates
 * are found, the primary CTA is "Wipe & Re-fetch from ArcGIS" which
 * navigates to the Parcel Converter page where the new job appears in
 * progress.
 *
 * Strict-equality dupes only (same lat + lng + normalized address) per
 * the original product spec — see services/parcel_audit_service.py for
 * the aggregation pipeline.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, ShieldAlert, MapPin, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { safeJsonFetch, authHeaders } from '../../lib/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const AuditDuplicatesDialog = ({ countyId, countyName, isAutoFetchable, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  // When the backend kicks off a background scan we land in `polling`
  // mode and hit `/audit/latest` every 3 s until status transitions
  // from 'running' → 'completed' / 'failed'. The header shows a
  // "Scanning…" indicator instead of the result cards while polling.
  const [polling, setPolling] = useState(false);

  const runAudit = useCallback(async () => {
    setLoading(true);
    setPolling(false);
    setError(null);
    setReport(null);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/audit`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    });
    if (res.ok && res.data) {
      // Backend always returns immediately now. If status=='running' we
      // need to poll until the background task completes. If the
      // response already has duplicate_groups (e.g. tiny county that
      // finished synchronously), use it directly.
      if (res.data.status === 'running') {
        setLoading(false);
        setPolling(true);
      } else {
        setLoading(false);
        setReport(res.data);
      }
    } else {
      setLoading(false);
      setError(res.data?.detail || res.error || `HTTP ${res.status}`);
    }
  }, [countyId]);

  useEffect(() => {
    runAudit();
  }, [runAudit]);

  // Poll /audit/latest every 3 s while the background scan is running.
  // Stop on status='completed'|'failed' (or dialog close — the parent
  // controls our mount lifecycle).
  useEffect(() => {
    if (!polling) return;
    let cancelled = false;
    const tick = async () => {
      const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/audit/latest`, {
        headers: authHeaders(),
      });
      if (cancelled) return;
      if (res.ok && res.data?.has_report) {
        const s = res.data.status;
        if (s === 'completed') {
          setPolling(false);
          setReport(res.data);
        } else if (s === 'failed') {
          setPolling(false);
          setError(res.data.error_message || 'Background audit failed.');
        }
        // else status==='running' → keep polling
      }
    };
    const interval = setInterval(tick, 3000);
    // Run immediately so we don't wait a full 3s for the first check
    tick();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [polling, countyId]);

  const startRebuild = async () => {
    if (!window.confirm(
      `This will DELETE all ${report?.total_parcels?.toLocaleString() || 'existing'} parcels for ${countyName} and re-fetch fresh data from ArcGIS. The new fetch runs in the background and may take 10–60 minutes for large counties. Continue?`
    )) return;
    setRebuilding(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/county/${countyId}/rebuild`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    });
    setRebuilding(false);
    if (res.ok && res.data?.job_id) {
      onClose?.();
      navigate('/admin/parcel-converter');
    } else {
      setError(res.data?.detail || res.error || `HTTP ${res.status}`);
    }
  };

  const hasDupes = !!(report && report.duplicate_groups > 0);
  const isHealthy = report && report.duplicate_groups === 0 && (report.coverage_pct === null || report.coverage_in_expected_range);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="audit-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={
              hasDupes
                ? 'p-2 rounded-lg bg-red-100 text-red-700'
                : isHealthy
                  ? 'p-2 rounded-lg bg-emerald-100 text-emerald-700'
                  : 'p-2 rounded-lg bg-blue-100 text-blue-700'
            }>
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Parcel Data Audit — {countyName}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Detects exact-equality duplicates (same lat + lng + address).
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            data-testid="audit-close-btn"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {(loading || polling) && (
            <div className="py-10 flex flex-col items-center justify-center gap-3 text-gray-500" data-testid="audit-loading">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p className="font-semibold">
                {polling ? 'Scanning parcels in the background…' : 'Starting scan…'}
              </p>
              <p className="text-xs text-gray-400 text-center max-w-md">
                {polling
                  ? 'Aggregation runs server-side and may take 30–90 s on a 1M+ parcel county. Safe to leave this dialog open — we\'ll refresh automatically.'
                  : 'Kicking off the background scan.'}
              </p>
            </div>
          )}

          {!loading && !polling && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900 flex items-start gap-2" data-testid="audit-error">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {!loading && !polling && report && (
            <div className="space-y-4">
              {/* Headline cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total Parcels</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{report.total_parcels.toLocaleString()}</p>
                  {report.coverage_pct !== null && (
                    <p className={`text-xs mt-1 ${report.coverage_in_expected_range ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {report.coverage_pct}% of expected {report.expected_total.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className={`border rounded-lg p-3 ${hasDupes ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Duplicate Groups</p>
                  <p className={`text-xl font-bold mt-1 ${hasDupes ? 'text-red-700' : 'text-emerald-700'}`} data-testid="audit-duplicate-groups">
                    {report.duplicate_groups.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {hasDupes ? 'Locations appearing 2+ times' : 'No duplicates found'}
                  </p>
                </div>
                <div className={`border rounded-lg p-3 ${hasDupes ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Excess Parcels</p>
                  <p className={`text-xl font-bold mt-1 ${hasDupes ? 'text-amber-700' : 'text-gray-700'}`} data-testid="audit-excess-parcels">
                    {report.excess_parcels.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Removed if deduped
                  </p>
                </div>
              </div>

              {/* Healthy state */}
              {isHealthy && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-2" data-testid="audit-healthy">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-emerald-900">
                    <p className="font-semibold">Parcel data is clean.</p>
                    <p className="mt-0.5">No duplicate parcels detected and the count is within the expected range. No action needed.</p>
                  </div>
                </div>
              )}

              {/* Overcount warning (no dupes but coverage > 120% or < 80%) */}
              {!hasDupes && report.coverage_pct !== null && !report.coverage_in_expected_range && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-900">
                    <p className="font-semibold">
                      Parcel count is {report.coverage_pct > 120 ? 'higher' : 'lower'} than expected ({report.coverage_pct}%).
                    </p>
                    <p className="mt-0.5">
                      No exact-duplicate parcels found, so the count drift is from a different source (e.g. commercial parcels imported as residential, or stale expected-total in the registry). Manual review recommended.
                    </p>
                  </div>
                </div>
              )}

              {/* Duplicate samples */}
              {hasDupes && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-red-900 text-sm flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      Top {Math.min(report.samples.length, report.sample_limit)} duplicate locations
                    </p>
                    {report.duplicate_groups > report.samples.length && (
                      <Badge className="bg-red-100 text-red-800 border border-red-300">
                        + {(report.duplicate_groups - report.samples.length).toLocaleString()} more
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="audit-samples">
                    {report.samples.map((s, idx) => (
                      <div key={idx} className="bg-white border border-red-200 rounded p-2 text-xs flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-gray-900 truncate">
                            <MapPin className="w-3 h-3 inline mr-1 text-red-600" />
                            {s.full_address || '(blank address)'}
                          </p>
                          <p className="text-gray-500 text-[11px] mt-0.5">
                            {s.latitude}, {s.longitude}
                          </p>
                        </div>
                        <Badge className="bg-red-200 text-red-900 border border-red-300 flex-shrink-0">
                          {s.count}× copies
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Remediation CTA */}
              {hasDupes && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
                  <p className="font-semibold text-blue-900 text-sm">Remediation</p>
                  <p className="text-xs text-blue-900 mt-1 mb-3">
                    Click below to <strong>delete every parcel in {countyName} and re-fetch fresh data from ArcGIS</strong>.
                    The new fetch runs in the background — Parcel Converter will show progress.
                  </p>
                  {isAutoFetchable ? (
                    <Button
                      onClick={startRebuild}
                      disabled={rebuilding}
                      className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                      data-testid="audit-rebuild-btn"
                    >
                      {rebuilding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting rebuild…</> : 'Wipe & Re-fetch from ArcGIS'}
                    </Button>
                  ) : (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                      ⚠ This county has no ArcGIS URL configured. Use the Parcel Data Mgmt page to upload a clean shapefile manually.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {report?.audited_at && `Audited ${new Date(report.audited_at).toLocaleString()} · ${report.duration_ms} ms`}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={runAudit} disabled={loading || polling} data-testid="audit-rerun-btn">
              {loading || polling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
              Re-scan
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="audit-cancel-btn">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditDuplicatesDialog;
